import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import HeaderComponent from "components/base/Header";

// CONTAINERS
const Container = tw.div`relative`;
const SearchContainer = tw.div`flex justify-start`;
const PaginationContainer = tw.div`flex justify-center`;
const ContentWithPaddingXl= tw.div`max-w-screen-xl mx-auto`;
const ContentWithPaddingLg= tw.div`max-w-screen-lg mx-auto py-20 lg:py-24`;
const ContentWithVerticalPadding = tw.div`py-20 lg:py-24`;
const Content2Xl= tw.div`max-w-screen-2xl mx-auto`;
const Posts = tw.div`mt-6 sm:-mr-8 flex flex-wrap justify-center`;
const PostContainer = styled.div`
	${tw`mt-2 w-3/4 sm:w-1/2 lg:w-1/3 sm:pr-8`}
	${(props) =>
		props.featured &&
		css`
			${tw`w-full!`}
			${Post} {
				${tw`sm:flex-row! h-full sm:pr-4`}
			}
			${Image} {
				${tw`sm:h-96 sm:min-h-full sm:w-1/2 lg:w-2/3 sm:rounded-t-none sm:rounded-l-lg`}
			}
			${Info} {
				${tw`sm:-mr-4 sm:pl-8 sm:flex-1 sm:rounded-none sm:rounded-r-lg sm:border-t-2 sm:border-l-2`}
			}
			${Description} {
				${tw`text-sm mt-3 leading-loose text-gray-600 font-medium`}
			}
		`}
	`;

export const ContainerStyles = { Container, SearchContainer, PaginationContainer, ContentWithPaddingXl, ContentWithPaddingLg, ContentWithVerticalPadding, Content2Xl, Posts, PostContainer }

// BUTTONS
const PrimaryButton = tw.button`px-6 py-2 rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300`;

// TEXT
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const SearchInput = tw.input`w-full px-6 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-4 first:mt-0`;
const Title = tw.div`mt-1 font-bold text-xl text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Description = tw.div``;
const Info = tw.div`p-2 border-2 rounded-t-lg rounded-l-lg`;
const Category = tw.div`uppercase text-primary-500 text-xs font-bold tracking-widest leading-loose after:content after:block after:border-b-2 after:border-primary-500 after:w-8`;
const Post = tw.div`cursor-pointer flex flex-col bg-gray-100`;
const Image = styled.div`
	${(props) =>
		css`
			background-image: url("${props.imageSrc}");
		`}
	${tw`h-64 w-full bg-cover bg-center rounded-t-lg`}
`;
const Text = styled.div`
  ${tw`text-lg  text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

export const TextStyles = { HighlightedText, SearchInput, Title, Description, Post, Image, Info, Category, Text }

// HEADINGS
const HeadingRow = tw.div`flex justify-between items-center sm:flex-col md:flex-row mb-4`;
const Heading = tw.h2`text-2xl sm:text-4xl font-black tracking-wide text-center text-gray-900`;
const SectionHeading = tw.h2`text-4xl sm:text-5xl font-black tracking-wide text-center`
const Subheading = tw.h5`font-bold text-primary-500`

export const HeadingStyles = { HeadingRow, Heading, SectionHeading, Subheading }

// NAVIGATION
const NavLinks = tw.div`inline-block items-center`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

const LogoLink = styled(NavLink)`
	${tw`flex items-center border-b-0 ml-0! pb-4`};
`;

const MobileNavLinksContainer = tw.nav`lg:hidden flex flex-1 items-center justify-between`;
const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
const DesktopNavLinks = tw.nav`hidden lg:flex flex-1 justify-between items-center`;
const MobileNavLinks = motion(styled.div
    `${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
	${NavLinks} {
		${tw`flex flex-col items-center`}
	}`
);

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */
const collapseBreakPointCssMap = {
	sm: {
		mobileNavLinks: tw`sm:hidden`,
		desktopNavLinks: tw`sm:flex`,
		mobileNavLinksContainer: tw`sm:hidden`,
	},
	md: {
		mobileNavLinks: tw`md:hidden`,
		desktopNavLinks: tw`md:flex`,
		mobileNavLinksContainer: tw`md:hidden`,
	},
	lg: {
		mobileNavLinks: tw`lg:hidden`,
		desktopNavLinks: tw`lg:flex`,
		mobileNavLinksContainer: tw`lg:hidden`,
	},
	xl: {
		mobileNavLinks: tw`lg:hidden`,
		desktopNavLinks: tw`lg:flex`,
		mobileNavLinksContainer: tw`lg:hidden`,
	},
};

export const NavStyles = { NavLinks, NavLink, PrimaryLink, LogoLink, MobileNavLinksContainer, NavToggle, DesktopNavLinks, MobileNavLinks, collapseBreakPointCssMap }


// HEADER
const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto p-0
`;

const StyledHeader = styled(HeaderComponent)`
	${tw`p-4 max-w-none mx-0 w-full bg-primary-900 mb-12`}
	${DesktopNavLinks} ${NavLink}, ${LogoLink} {
		${tw`text-gray-100 hover:border-gray-300 hover:text-gray-300`}
	}
	${NavToggle}.closed {
		${tw`text-gray-100 hover:text-primary-500`}
	}
`;

const ContentStyledHeader = styled(HeaderComponent)`
	${tw`p-0 max-w-none w-full`}
	${LogoLink} {
		${tw`text-black hover:border-gray-300 hover:text-gray-300`}
	}
`;

export const HeaderStyles = { Header, StyledHeader, ContentStyledHeader }


// CAMPAIGN
const Logo = tw.img`w-20 mr-3`;
const MainTitle = tw.p`font-black text-2xl!`;
const Theme = tw.p`font-bold text-sm`;

export const CampaignStyles = { Logo, MainTitle, Theme }


// HOMEMENU
const ControlButton = styled(PrimaryButton)`
	${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
	svg {
		${tw`w-6 h-6`}
	}
`;
const ProfileButton = tw(ControlButton)`m-2 flex items-center outline p-2`;
const HomeContainer = styled.div`
	${tw`relative -mx-8 -my-8 bg-center bg-cover h-screen min-h-144 overflow-auto`}
	background-image: url(${(props) => props.background});
`;
const HeroContainer = tw(
	Container
)`z-20 mx-auto min-h-full flex flex-col overflow-auto sm:overflow-hidden`;
const MenuContent = tw.div`px-4 pb-8 sm:mt-12 flex flex-1 flex-col justify-center sm:justify-normal items-center `;
const MenuGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-4`;
const MenuPrimaryAction = tw.a`
  rounded-full px-8 py-3 text-sm sm:text-base sm:mt-2 sm:px-8 sm:py-4 bg-gray-100 
  font-bold shadow transition duration-300 bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 
  focus:outline-none focus:shadow-outline text-center
`;

export const MenuStyles = { ProfileButton, HomeContainer, HeroContainer, MenuContent, MenuGrid, MenuPrimaryAction }


// FOOTER
const FooterContainer = tw(Container)`bg-primary-900 text-white -mx-8 -mb-8 relative`
const FooterContent = tw.div`max-w-screen-xl mx-auto py-6`;
const FooterRow = tw.div`flex items-center justify-center flex-col`
const FooterLinksContainer = tw.div`mt-4 font-medium flex flex-wrap justify-center items-center flex-col sm:flex-row`
const FooterLink = tw.a`border-b-2 border-transparent hocus:text-gray-300 hocus:border-gray-300 pb-1 transition duration-300 mt-2 mx-4`;
const FooterCopyrightText = tw.p`text-center m-4 font-medium text-sm text-white`

export const FooterStyles = { FooterContainer, FooterContent, FooterRow, FooterLink, FooterLinksContainer, FooterCopyrightText }

// VIDEO
const VideoContainer = styled.div`
	width: 100%;
	height: 0;
	padding-bottom: 56.25%;
	position: relative;
	overflow: hidden;

	@media (max-width: 768px) {
		padding-bottom: 55%;
	}

	.swarm-fluid {
		width: 100%;
		height: 100%;
		object-fit: contain; /* Or cover, depending on your preference */
	}
`;

export const VideoStyles = { VideoContainer }

// CAROUSEL
const VideoCardTitle = tw.p`text-sm sm:text-base md:text-sm font-medium text-black leading-tight line-clamp-4 md:line-clamp-2`
const VideoCardDet1 = tw.p`text-xs text-gray-600 mt-1`
const VideoCardDet2 = tw.p`text-xs text-gray-600`
const VideoDurationBox = tw.span`absolute bottom-0 m-1 right-0 bg-black text-white text-xs px-1 py-1 rounded`
const VideoThumbnail = tw.img`object-cover w-full h-full`
const VideoPlayer = tw.div`bg-gray-200 border rounded-lg overflow-hidden relative aspect-video`
const VideoCardContainer = tw.div`p-2`

// Mobile layout (YouTube style list)
const MobileCard = tw.div`flex gap-3 items-start md:hidden`
const MobileThumbWrapper = tw.div`max-w-32 h-full flex-shrink-0 rounded-lg overflow-hidden relative`
const MobileThumb = tw.img`object-cover w-full h-full rounded-lg`

// Desktop layout (grid/card style)
const DesktopCard = tw.div`hidden md:block`
const CardContentContainer = tw.div`mt-2`
const CarouselPost = tw.div`cursor-pointer flex flex-col bg-gray-100 mt-6 mb-6`;

export const CarouselStyles = { VideoCardTitle, VideoCardDet1, VideoCardDet2, VideoDurationBox, VideoThumbnail, VideoPlayer, VideoCardContainer, MobileCard, MobileThumbWrapper, MobileThumb, DesktopCard, CardContentContainer, CarouselPost }

// AUDIO
const Section = tw.div`flex-1 overflow-y-auto space-y-2 text-lg text-center`;
const PlayerCard = tw.div`
  h-full flex flex-col justify-between bg-white rounded-2xl shadow-lg p-0 sm:p-6 w-full mx-auto
`;
const SongInfo = tw.div`flex items-center space-x-4 mb-4`;
const AlbumCover = tw.img`w-12 h-12 rounded-lg shadow`;
const SongText = tw.div`text-left`;
const AudioControllerCont = tw.div`flex justify-around items-center flex-col sm:flex-row`;
const SongTitle = tw.h3`text-lg font-bold text-gray-800`;
const SongArtist = tw.p`text-sm text-gray-500`;
const LyricsBox = tw.div`h-[65vh] overflow-y-auto border rounded-lg p-3 bg-gray-100 text-gray-700 leading-relaxed text-base sm:text-lg md:text-xl mb-5`;
const SongControls = tw.div`flex items-center justify-center space-x-6 mt-2 mb-4`;
const SongControlButton = tw.button`p-3 rounded-full bg-gray-100 hover:bg-gray-200 shadow text-lg text-gray-800`;
const PlayButton = tw.button`p-4 rounded-full bg-primary-900 hover:bg-pink-700 text-white shadow-lg text-xl`;
const ProgressWrapper = tw.div`mt-2 md:w-[70%] w-[100%]`;
const ProgressBar = tw.input`w-full accent-primary-600`;
const TimeRow = tw.div`flex justify-between text-xs text-gray-500`;
const VolumeRow = tw.div`mt-3 flex items-center space-x-2 max-w-xs`;
const VolumeSlider = tw.input`w-full accent-primary-600`;

export const AudioStyles = { Section, PlayerCard, SongInfo, AlbumCover, SongText, SongTitle, SongArtist, LyricsBox, SongControls,AudioControllerCont, SongControlButton, PlayButton, ProgressWrapper, ProgressBar, TimeRow, VolumeRow, VolumeSlider }

// FORMS
const FormContainer = tw.div`w-full max-w-4xl mx-auto`;
const Form = tw.form`w-full`;
const FormRow = tw.div`mb-2`;
const FormLabel = tw.label`text-lg font-semibold text-gray-900 block`;
const FormSubmitButton = tw.button`px-6 py-3 rounded bg-primary-500 text-gray-100 hover:bg-primary-700 hover:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 w-full max-w-xs mt-6`;
const FormHeader = tw.div`flex justify-between items-center mb-6`;
const FormTitle = tw.h2`text-2xl font-bold text-gray-900`;
const FormBackButton = tw.button`text-gray-500 hover:text-gray-700`;
const FormButtonGroup = tw.div`gap-4`;
const FormCancelButton = tw.button`px-6 py-3 ml-3 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-300`;
const FormStaticSection = tw.div`mb-8 p-6 bg-gray-100 rounded-lg`;
const FormStaticTitle = tw.h3`text-xl font-semibold text-gray-900 mb-4`;
const FormStaticGrid = tw.div`grid grid-cols-1 md:grid-cols-3 gap-4`;
const FormStaticField = tw.div``;
const FormPendingNotification = tw.div`mb-6 p-4 bg-yellow-100 border border-yellow-200 rounded-lg`;
const FormPendingContent = tw.div`flex items-center`;
const FormPendingIcon = tw.div`w-4 h-4 bg-yellow-400 rounded-full mr-3`;
const FormPendingText = tw.div``;
const FormPendingTitle = tw.p`text-yellow-800 font-medium`;
const FormPendingDescription = tw.p`text-yellow-600 text-sm`;

export const FormStyles = { FormContainer, Form, FormRow, FormLabel, FormSubmitButton, FormHeader, FormTitle, FormBackButton, FormButtonGroup, FormCancelButton, FormStaticSection, FormStaticTitle, FormStaticGrid, FormStaticField, FormPendingNotification, FormPendingContent, FormPendingIcon, FormPendingText, FormPendingTitle, FormPendingDescription }


// DATA TABLE
const DataTableContainer = tw.div`mb-8`;
const DataTableHeader = tw.div`flex justify-between items-center mb-4`;
const DataTableTitle = tw.h3`text-xl font-semibold text-gray-900`;
const DataTableAddButton = tw(FormSubmitButton)`max-w-none w-auto px-4 py-2 mt-0`;
const DataTableEmptyState = tw.div`text-center py-8 text-gray-500`;
const DataTableWrapper = tw.div`overflow-x-auto`;
const DataTable = tw.table`w-full border-collapse border border-gray-300`;
const DataTableHead = tw.thead``;
const DataTableHeaderRow = tw.tr`bg-gray-100`;
const DataTableHeaderCell = tw.th`border border-gray-300 px-4 py-2 text-left font-semibold`;
const DataTableBody = tw.tbody``;
const DataTableRow = tw.tr`hover:bg-gray-500`;
const DataTableCell = tw.td`border border-gray-300 px-4 py-2`;
const DataTableEditButton = tw.button`text-primary-500 hover:text-primary-700 font-medium`;

export const DataTableStyles = { DataTableContainer, DataTableHeader, DataTableTitle, DataTableAddButton, DataTableEmptyState, DataTableWrapper, DataTable, DataTableHead, DataTableHeaderRow, DataTableHeaderCell, DataTableBody, DataTableRow, DataTableCell, DataTableEditButton }

// FAQ
const FAQContent = tw.div`lg:ml-12`;
const FAQSubheading = tw(Subheading)`text-center lg:text-left`;
const FAQDescription = tw.p`max-w-xl text-center mx-auto lg:mx-0 lg:text-left lg:max-w-none leading-relaxed text-sm sm:text-base lg:text-lg font-medium mt-4 text-secondary-100`;

const FAQSContainer = tw.dl`mt-8`;
const FAQContainer = tw.div`mt-8 border lg:border-0 px-8 py-4 lg:p-0 rounded-lg lg:rounded-none`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-lg lg:text-xl font-semibold`;
const QuestionToggleIcon = styled.span`
	${tw`cursor-pointer ml-2 bg-primary-500 text-gray-100 p-1 rounded-full group-hover:bg-primary-700 group-hover:text-gray-200 transition duration-300`}
	svg {
		${tw`w-4 h-4`}
	}
`;
// Removed pointer-events-none from the Answer component
const Answer = motion(styled.div`
	${tw`text-sm sm:text-base leading-relaxed`}
	white-space: pre-line;
`);

export const FAQStyles = { FAQContent, FAQSubheading, FAQDescription, FAQSContainer, FAQContainer, Question, QuestionText, QuestionToggleIcon, Answer }
